import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CreateTenantData {
  name: string;
  slug: string;
  is_active: boolean;
}

interface UpdateTenantData extends CreateTenantData {
  id: string;
}

export function useTenants() {
  const queryClient = useQueryClient();

  // Fetch all tenants (super admin only)
  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Tenant[];
    },
  });

  // Fetch user counts per tenant
  const { data: userCounts = {} } = useQuery({
    queryKey: ['tenant-user-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('tenant_id');

      if (error) throw error;

      const counts: Record<string, number> = {};
      data.forEach((profile) => {
        counts[profile.tenant_id] = (counts[profile.tenant_id] || 0) + 1;
      });

      return counts;
    },
  });

  // Create tenant mutation
  const createTenant = useMutation({
    mutationFn: async (data: CreateTenantData) => {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return tenant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create tenant');
    },
  });

  // Update tenant mutation
  const updateTenant = useMutation({
    mutationFn: async ({ id, ...data }: UpdateTenantData) => {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return tenant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update tenant');
    },
  });

  // Toggle tenant status
  const toggleTenantStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('tenants')
        .update({ is_active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant status updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update tenant status');
    },
  });

  return {
    tenants,
    isLoading,
    userCounts,
    createTenant,
    updateTenant,
    toggleTenantStatus,
  };
}
